import useAuthenticatedApi from '@/src/Authentication/components/hooks/useAuthenticatedApi';

export type CropGravity =
    | 'center'
    | 'north'
    | 'south'
    | 'east'
    | 'west'
    | 'northwest'
    | 'northeast'
    | 'southwest'
    | 'southeast';

export interface CropSpec {
    type: 'crop';
    aspectRatio: number;
    gravity: CropGravity;
}

export interface FlowStep {
    name: string;
    [key: string]: unknown;
}

export interface FlowSpec {
    type: 'flow';
    flowDefinition: FlowStep[];
    label?: string;
}

export type EditSpec = CropSpec | FlowSpec;

export interface GalleryFlow {
    id: number;
    galleryId: number;
    enabled: boolean;
    environmentId: number;
    flowDefinition: string;
    template?: string | null;
}

export interface ProcessorInfo {
    name: string;
    environments: string[];
    title?: string;
    description?: string;
}

export interface EditBatch {
    id: number;
    activationId: number;
    status:
        | 'pending'
        | 'processing'
        | 'completed'
        | 'completed_with_errors'
        | 'failed'
        | 'rolled_back';
    spec: EditSpec;
    totalCount: number;
    succeededCount: number;
    failedCount: number;
    error: string | null;
    createdAt: string;
    items?: EditBatchItem[];
}

export interface EditBatchItem {
    id: number;
    batchId: number;
    mediaId: number;
    status: 'pending' | 'succeeded' | 'failed' | 'rolled_back';
    error: string | null;
    resultVersion: number | null;
}

export interface MediaVersion {
    id: number;
    mediaId: number;
    versionNumber: number;
    originalUri: string;
    thumbnailUri: string;
    thumbnail_800pxUri: string;
    thumbnailTinyUri: string;
    aspectRatio: number | null;
    mimeType: string | null;
    source: string;
    editBatchId: number | null;
    createdBy: string | null;
    createdAt: string;
}

/** CSS object-position for previewing a gravity crop with object-fit: cover. */
export function gravityToObjectPosition(gravity: CropGravity): string {
    const x = gravity.includes('west')
        ? '0%'
        : gravity.includes('east')
        ? '100%'
        : '50%';
    const y = gravity.includes('north')
        ? '0%'
        : gravity.includes('south')
        ? '100%'
        : '50%';
    return `${x} ${y}`;
}

/**
 * Bulk inline media editing: create a crop batch over media ids, poll it,
 * roll it back, and browse/restore per-media version history. Edits are
 * versioned pointer swaps server-side — guests see the new state on next
 * load, and every prior state stays recallable forever.
 */
export default function useMediaEdit() {
    const authenticatedApi = useAuthenticatedApi();

    const createEditBatch = async (
        activationId: number,
        mediaIds: number[],
        spec: EditSpec,
    ): Promise<EditBatch> => {
        const response = await authenticatedApi.post('/media/edit-batch', {
            activationId,
            mediaIds,
            spec,
        });
        return response.data;
    };

    const getEditBatch = async (batchId: number): Promise<EditBatch> => {
        const response = await authenticatedApi.get(
            `/media/edit-batch/${batchId}`,
        );
        return response.data;
    };

    const listEditBatches = async (
        activationId: number,
    ): Promise<EditBatch[]> => {
        const response = await authenticatedApi.get(
            `/media/edit-batch/activation/${activationId}`,
        );
        return Array.isArray(response.data) ? response.data : [];
    };

    const rollbackEditBatch = async (batchId: number): Promise<EditBatch> => {
        const response = await authenticatedApi.post(
            `/media/edit-batch/${batchId}/rollback`,
        );
        return response.data;
    };

    const listVersions = async (
        mediaId: number,
    ): Promise<{
        mediaId: number;
        currentVersion: number | null;
        versions: MediaVersion[];
    }> => {
        const response = await authenticatedApi.get(
            `/media/${mediaId}/versions`,
        );
        return response.data;
    };

    const restoreVersion = async (mediaId: number, versionNumber: number) => {
        const response = await authenticatedApi.post(
            `/media/${mediaId}/restore/${versionNumber}`,
        );
        return response.data;
    };

    /** Server-rendered throwaway preview (800px thumb → test/ S3 key). */
    const previewEdit = async (
        mediaId: number,
        spec: EditSpec,
    ): Promise<{ url: string }> => {
        const response = await authenticatedApi.post(
            `/media/${mediaId}/edit-preview`,
            { spec },
        );
        return response.data;
    };

    /** Saved processing flows for a gallery (flowDefinition is a JSON string). */
    const listGalleryFlows = async (
        galleryId: number,
    ): Promise<GalleryFlow[]> => {
        const response = await authenticatedApi.get(
            `/processing/gallery/${galleryId}`,
        );
        return Array.isArray(response.data) ? response.data : [];
    };

    /** Processor registry metadata (names + supported environments). */
    const listProcessors = async (): Promise<ProcessorInfo[]> => {
        const response = await authenticatedApi.get('/processing/processors');
        return Array.isArray(response.data) ? response.data : [];
    };

    return {
        createEditBatch,
        getEditBatch,
        listEditBatches,
        rollbackEditBatch,
        listVersions,
        restoreVersion,
        previewEdit,
        listGalleryFlows,
        listProcessors,
    };
}
